import { useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Share2, Cpu, Gauge, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useCatalog } from "../lib/useCatalog";
import { useHardware } from "../context/HardwareContext";
import { bestVerdict, evaluate, recommendedQuant } from "../lib/compute";
import { decodeHardware } from "../components/ShareDiagnostic";
import { VerdictBadge } from "../components/VerdictBadge";
import { Button } from "../components/ui/button";

const ORDER = { can_run: 0, tight: 1, no_go: 2 };

function useSharedHardware() {
  const [params] = useSearchParams();
  return useMemo(() => {
    try {
      return decodeHardware(params.get("hw") || "");
    } catch {
      return null;
    }
  }, [params]);
}

function Spec({ label, value }) {
  return (
    <div className="rounded-lg border border-[#1E2638] bg-[#080B10] px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="font-mono2 text-sm text-slate-200">{value}</div>
    </div>
  );
}

export default function SharedDiagnostic() {
  const hw = useSharedHardware();
  const { data } = useCatalog();
  const { hardware: mine, applyHardware } = useHardware();
  const navigate = useNavigate();

  const rows = useMemo(() => {
    if (!data || !hw) return [];
    return data.models
      .map((m) => {
        const q = recommendedQuant(m, hw, data.quantizations);
        return { m, q, ev: evaluate(m, q, hw), mine: bestVerdict(m, mine, data.quantizations) };
      })
      .filter((r) => r.ev.verdict !== "no_go")
      .sort((a, b) => ORDER[a.ev.verdict] - ORDER[b.ev.verdict] || b.m.params - a.m.params);
  }, [data, hw, mine]);

  if (!hw) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10">
        <p data-testid="shared-diagnostic-error" className="rounded-xl border border-red-900 bg-red-950/30 p-4 text-sm text-red-400">Ce lien de diagnostic est illisible.</p>
      </div>
    );
  }

  const use = () => {
    applyHardware(hw);
    toast.success("Machine appliquée à votre diagnostic");
    navigate("/");
  };
  const gpuText = hw.accelerator === "cpu" ? "Aucun GPU" : `${hw.gpu}${hw.gpuCount > 1 && !/^\d+\s*×/.test(hw.gpu) ? ` ×${hw.gpuCount}` : ""}`;

  return (
    <div className="mx-auto max-w-6xl space-y-6 px-4 py-10 md:px-8" data-testid="shared-diagnostic-page">
      <div>
        <span className="flex items-center gap-1.5 font-mono2 text-xs uppercase tracking-widest text-primary"><Share2 className="h-4 w-4" /> Diagnostic partagé</span>
        <h1 className="mt-2 font-head text-3xl font-extrabold tracking-tight">Ce que cette machine peut faire tourner</h1>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5" data-testid="shared-diagnostic-specs">
        <Spec label="CPU" value={`${hw.cpuCores} cœurs`} />
        <Spec label="RAM" value={`${hw.ram} Go`} />
        <Spec label="GPU" value={gpuText} />
        <Spec label="VRAM" value={hw.accelerator === "cpu" ? "—" : hw.gpuCount > 1 ? `${hw.vram} Go ×${hw.gpuCount}` : `${hw.vram} Go`} />
        <Spec label="Accélérateur" value={hw.accelerator} />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={use} data-testid="shared-diagnostic-use-button" className="gap-1.5"><Cpu className="h-4 w-4" /> Utiliser cette machine</Button>
        <Link to="/" className="flex items-center gap-1 text-sm text-primary hover:underline">Faire mon propre diagnostic <ArrowRight className="h-4 w-4" /></Link>
        <span className="text-sm text-muted-foreground" data-testid="shared-diagnostic-count">{data ? `${rows.length} modèle(s) compatible(s)` : "Calcul en cours…"}</span>
      </div>
      <div className="overflow-x-auto rounded-xl border border-[#1E2638] bg-[#0F141C]">
        <table className="w-full text-sm" data-testid="shared-diagnostic-table">
          <thead className="border-b border-[#1E2638] text-left text-[11px] uppercase tracking-wide text-muted-foreground">
            <tr><th className="px-4 py-2">Modèle</th><th className="px-4 py-2">Cette machine</th><th className="px-4 py-2">Quantif. · mémoire</th><th className="px-4 py-2">Vitesse</th><th className="px-4 py-2">Ma machine</th></tr>
          </thead>
          <tbody className="divide-y divide-[#1E2638]/60">
            {rows.map(({ m, q, ev, mine: mv }) => (
              <tr key={m.id} data-testid="shared-diagnostic-row">
                <td className="px-4 py-2"><span className="font-medium">{m.name}</span> <span className="font-mono2 text-xs text-muted-foreground">{m.params}B</span></td>
                <td className="px-4 py-2"><VerdictBadge verdict={ev.verdict} /></td>
                <td className="px-4 py-2 font-mono2 text-xs">{q.label} · ~{ev.requiredGB.toFixed(1)} Go</td>
                <td className="px-4 py-2 font-mono2 text-xs"><Gauge className="inline h-3 w-3" /> ~{ev.tokPerSec} tok/s</td>
                <td className="px-4 py-2"><VerdictBadge verdict={mv} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
